'use client';

import React, { useEffect, useState } from 'react';
import { useParams } from 'next/navigation';
import { Dialog, Input, PulseButton } from 'src/components';
import {
  useGetCompanyByIdQuery,
  useConvertDemoCompanyMutation,
} from 'src/api/redux';
import { useLogging } from 'src/context/logging';
import { useNotification } from 'src/context/notification';

interface ConvertDemoDialogProps {
  open: boolean;
  onClose: () => void;
}

const ConvertDemoDialog = ({ open, onClose }: ConvertDemoDialogProps) => {
  const { companyId } = useParams<{ companyId: string }>();
  const log = useLogging();
  const { setNotification } = useNotification();

  const { data: currentCompany } = useGetCompanyByIdQuery(
    { companyId },
    { skip: !companyId }
  );
  const [convertDemoCompany] = useConvertDemoCompanyMutation();

  const [isLoading, setIsLoading] = useState(false);
  const [name, setName] = useState('');

  useEffect(() => {
    if (open && currentCompany?.name) {
      setName(currentCompany.name);
    }
  }, [open, currentCompany?.name]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && name.trim() && !isLoading) {
      handleConvert();
    }
  };

  const handleConvert = async () => {
    if (!companyId || !name.trim()) return;

    try {
      setIsLoading(true);
      await convertDemoCompany({
        companyId,
        name: name.trim(),
      }).unwrap();

      setNotification({
        title: 'Success',
        message: 'Company converted to customer successfully',
        type: 'success',
      });
      setName('');
      onClose();
    } catch (err: any) {
      log.exception('Failed to convert demo company', err);
      setNotification({
        title: 'Error',
        message:
          err?.data?.error ||
          err?.data?.error_message ||
          err?.message ||
          'Failed to convert demo company',
        type: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setName('');
    onClose();
  };

  return (
    <Dialog
      size="lg"
      isOpen={open}
      onClose={handleClose}
      showCloseButton={true}
      aria-labelledby="convert-demo-dialog-title"
      aria-describedby="convert-demo-dialog-description"
      data-testid="convert-demo-dialog"
    >
      <Dialog.Header>
        <div
          id="convert-demo-dialog-title"
          className="text-lg font-medium text-neutral-600"
          data-testid="convert-demo-dialog-header"
        >
          Convert to Customer
        </div>
      </Dialog.Header>
      <Dialog.Content>
        <div
          className="flex flex-col gap-4 p-4"
          data-testid="convert-demo-dialog-content"
        >
          <span
            id="convert-demo-dialog-description"
            className="px-4 stext-sm font-medium text-secondary "
          >
            Convert <strong>{currentCompany?.name}</strong> from a demo account
            to a full customer account. This will remove all demo users and
            data.
          </span>
          <div className="px-4 flex flex-col gap-1">
            <label
              htmlFor="convert-company-name"
              className="block text-sm font-semibold text-primary"
            >
              New Company Name
            </label>
            <Input
              id="convert-company-name"
              type="text"
              value={name}
              onChange={handleNameChange}
              onKeyDown={handleKeyDown}
              placeholder="Enter company name"
              data-testid="convert-demo-dialog-name-input"
              autoFocus
            />
          </div>
        </div>
      </Dialog.Content>
      <Dialog.Actions>
        <div className="flex flex-row gap-2">
          <PulseButton
            variant="outline"
            color="neutral"
            onClick={handleClose}
            data-testid="convert-demo-dialog-cancel"
          >
            Cancel
          </PulseButton>
          <PulseButton
            variant="solid"
            color="primary"
            onClick={handleConvert}
            disabled={!name.trim()}
            loading={isLoading}
            data-testid="convert-demo-dialog-confirm"
            aria-label={`Convert ${
              currentCompany?.name || 'company'
            } to customer`}
          >
            Convert
          </PulseButton>
        </div>
      </Dialog.Actions>
    </Dialog>
  );
};

export default ConvertDemoDialog;
